import type { ReactNode } from 'react';
import { Colophon } from '../sections/Colophon.js';
import type { WashedLocale } from '@washed/i18n';
import { MarketingNav } from '../components/MarketingNav.js';
import styles from './PageShell.module.css';

// Shared chrome for every dedicated content page (/tarifs, /faq, …): canonical
// WASHED marketing navigation + Colophon footer, plus an optional page header.
//
// Two layouts:
//  • heroHeader=true  — self-contained pages with a visible page header.
//  • heroHeader=false — pages that reuse a full-width homepage section.
//
// Navigation itself lives in MarketingNav so homepage and dedicated routes do
// not drift into separate brand systems.
export interface PageShellProps {
  locale: WashedLocale;
  onLocaleChange: (locale: WashedLocale) => void;
  /** The page <h1>. Visible when `heroHeader`, otherwise screen-reader-only by default. */
  title: string;
  eyebrow?: string;
  intro?: string;
  /** Render a visible page header (true) or use the full-width content layout (false). */
  heroHeader?: boolean;
  /** Set when a full-width child already renders the page's semantic <h1>. */
  childOwnsH1?: boolean;
  /** Show the standard "join the waitlist" CTA below the content. Default true. */
  cta?: boolean;
  /** Optional page-level accent theme. `brand` maps accent variables to WASHED green. */
  accent?: 'brand';
  children: ReactNode;
}

export function PageShell({
  locale,
  onLocaleChange,
  title,
  eyebrow,
  intro,
  heroHeader = true,
  childOwnsH1 = false,
  cta = true,
  accent,
  children,
}: PageShellProps) {
  const ctaLabel = locale === 'fr' ? "Rejoindre la liste d'attente" : 'Join the waitlist';
  const ctaHref = locale === 'fr' ? '/#rejoindre' : '/en#rejoindre';

  return (
    <div className={`${styles.page} ${accent === 'brand' ? styles.brandAccent : ''}`}>
      <MarketingNav locale={locale} onLocaleChange={onLocaleChange} context="page" />

      {heroHeader ? (
        <main id="contenu" tabIndex={-1} className={styles.main}>
          <header className={styles.head}>
            {eyebrow !== undefined && <p className={`smallcaps ${styles.eyebrow}`}>{eyebrow}</p>}
            <h1 className={styles.h1}>{title}</h1>
            {intro !== undefined && <p className={styles.intro}>{intro}</p>}
          </header>
          {children}
          {cta && (
            <div className={styles.cta}>
              <a href={ctaHref} className={styles.ctaButton}>
                {ctaLabel}
                <span aria-hidden="true"> →</span>
              </a>
            </div>
          )}
        </main>
      ) : (
        <main id="contenu" tabIndex={-1}>
          {!childOwnsH1 && <h1 className="sr-only">{title}</h1>}
          {children}
          {cta && (
            <div className={styles.ctaBand}>
              <a href={ctaHref} className={styles.ctaButton}>
                {ctaLabel}
                <span aria-hidden="true"> →</span>
              </a>
            </div>
          )}
        </main>
      )}

      <Colophon locale={locale} />
    </div>
  );
}
